// Message patient / Request insurance card 的草稿生成（原型 ASK 表 + requestBody，08/19 版逐字移植）。
// 草稿用 provider 第一人称 "I"；发送前可编辑。
import { ECoverageStatus, ENetworkStatus, TEligibilityCheckVo, TPayerCandidate } from 'types/insuranceEligibility';
import { TAaaEntry, TAaaFixField } from './aaa';
import { formatCheckedDate } from './derive';

type TAsk = {
    problem: string;
    ask: string;
    // when 存在 → 结尾追加 "I will re-check your coverage <when> …"；tail=false 的场景没有复查承诺
    when?: string;
};

const ASK: Record<TAaaFixField, TAsk> = {
    // "That does not mean you are uninsured" 是 Stedi 指南的患者侧原句：拒识 ≠ 失去保险
    memberId: {
        problem:
            'the member ID I have on file was not recognized. That does not mean you are uninsured, only that they could not look you up',
        ask: 'Could you upload a photo of the front and back of your current insurance card through your patient portal?',
        when: 'as soon as it comes through',
    },
    dob: {
        problem: 'the date of birth I have on file did not match their records',
        ask: 'Could you reply with your date of birth as it appears on your insurance card?',
        when: 'as soon as I hear back',
    },
    // 什么都没比对过（我们这边是空字段），文案不能说 "did not match"
    dobMissing: {
        problem: 'I do not have a usable date of birth for you on file',
        ask: 'Could you add it in your patient portal, exactly as it appears on your insurance card?',
        when: 'as soon as it is on file',
    },
    genderOnPolicy: {
        problem:
            'their records list a different gender than the one on your chart. Insurers use whatever is on the policy itself, which is often not the same thing',
        ask: 'Could you let me know which gender is listed on your plan? It will not change anything on your chart.',
        when: 'as soon as I hear back',
    },
};

export type TDraftContext = {
    patientFirstName: string;
    payerName: string;
    memberIdOnFile?: string;
    // AAA 修复卡入口：按下 Message patient 时对应的码
    aaa?: TAaaEntry;
    // 非 AAA 场景按结果推断（inactive / oon / nonCovered）
    latestCheck?: TEligibilityCheckVo;
    routedPayer?: TPayerCandidate;
    // 二次失败（仅 managed）：草稿整体换版——按字段的请求已被无视过，问的是就诊还办不办
    secondFailure?: { cashPriceCents?: number; visitAt?: string };
};

// 二次失败草稿（原型逐字移植）。措辞刻意不说「你没回我」：最常见的路径是患者回了、
// 改了资料、复查仍失败——失败的是 check 不是对话。
// ⚠️ 患者侧正式文案在 Figma 另行设计中，落地后整段覆盖本函数，不要逐句改
const buildSecondFailureDraft = (
    patientFirstName: string,
    ctx: NonNullable<TDraftContext['secondFailure']>,
): string => {
    const visit = ctx.visitAt ? `your ${formatCheckedDate(ctx.visitAt)} visit` : 'your upcoming visit';
    const cash = ctx.cashPriceCents != null ? `$${(ctx.cashPriceCents / 100).toLocaleString('en-US')}` : undefined;
    const ending = cash
        ? `You can still keep the appointment at my self-pay price of ${cash}. ` +
          'If I do not hear from you, the visit will be cancelled one hour before it starts and you will be refunded in full.\n\n' +
          'Just reply here and I will take care of it.'
        : 'If you have a different insurance card, or corrected details, reply here and I will re-check right away.\n\n' +
          'If I do not hear from you, the visit will be cancelled one hour before it starts, you will be refunded in full, ' +
          'and Klarity will help you find another provider.';
    return (
        `Hi ${patientFirstName},\n\n` +
        `I have not been able to confirm your insurance for ${visit}, even after checking again.\n\n${ending}`
    );
};

// Message patient 草稿正文
export const buildPatientDraft = (ctx: TDraftContext): string => {
    const { aaa, latestCheck } = ctx;
    const r = latestCheck?.result;
    let ask: TAsk;

    if (ctx.secondFailure) {
        return buildSecondFailureDraft(ctx.patientFirstName, ctx.secondFailure);
    }
    if (r?.serviceNotCovered) {
        ask = {
            problem:
                'they told us this plan does not cover it. Your insurance is still active, it just does not include this kind of visit',
            ask: 'If you have another insurance plan, could you send me the details? Otherwise let me know and I will tell you what the visit costs without insurance.',
        };
    } else if (r?.coverageStatus === ECoverageStatus.INACTIVE) {
        ask = {
            problem: 'they reported the plan as no longer active',
            ask: 'If you have changed insurance, could you let me know which plan you are on now?',
            when: 'as soon as it comes through',
        };
    } else if (r?.network?.status === ENetworkStatus.OUT_OF_NETWORK && r.network.planTypeNotOnTemplate) {
        // 10/05：OON 是 provider 自己的规则（service template 不收这类 plan），不是保险公司说的——
        // 话术按「我不收」说，不能推给 insurer。同样无复查承诺（原型 messagePatient 的 planType 分支，tail:false）
        ask = {
            problem:
                `I don’t take ${r.network.planTypeNotOnTemplate} plans, so I’m out of network for yours. ` +
                'Your coverage itself is active',
            ask:
                'If you have another plan I could bill, could you let me know? Otherwise I’d rather talk through the ' +
                'options before your visit than have a bill be the first you hear of it.',
        };
    } else if (r?.network?.status === ENetworkStatus.OUT_OF_NETWORK) {
        // 无复查承诺：网络状态不会自己变化，承诺再查是个没有机制的承诺
        ask = {
            problem: 'they list my practice as out of network for your plan',
            ask:
                'That usually means a visit costs more than it would in network, and with some plans it is not covered at all. ' +
                'Could you let me know whether you would like to go ahead on that basis, or whether you have another plan you would rather I bill?',
        };
    } else if (aaa?.fixField) {
        ask = ASK[aaa.fixField] || ASK.memberId;
    } else if (ctx.routedPayer) {
        ask = {
            problem: `I need to confirm which plan to bill it under. I am using ${ctx.routedPayer.name}, based on what I have on file`,
            ask: 'Could you confirm that is the right plan, and upload a photo of the front and back of your current card through your patient portal?',
        };
    } else {
        ask = ASK.memberId;
    }

    const memberBit = ctx.memberIdOnFile ? ` (member ID ${ctx.memberIdOnFile})` : '';
    const tail = ask.when ? ` I will re-check your coverage ${ask.when} and let you know what I find.` : '';
    return `Hi ${ctx.patientFirstName},\n\nI was checking your insurance coverage with ${ctx.payerName}${memberBit}, and ${ask.problem}.\n\n${ask.ask}${tail}`;
};

// 无码入口的语境：inactive = payer 答复了「计划已失效」（不是验证失败）；
// neutral = 常驻按钮（原型 #insActions），context-free——没查过/无保险/查过且通过的患者都可能点，
// 不能预设一次不存在的失败（原型把这两种都落 member-ID 兜底话术，是原型自带的瑕疵，2026-09-08 拍板修正）
// failedPlan：结果卡那张没查成功（不是 Eligible 都算，UNMAPPED 也算）→ 点名那张要新照片；neutral：没查过 / 查成功了
export type TCardRequestContext = 'failedPlan' | 'neutral';

// Request insurance card 的预填 note（原型 requestBody：系统解释为什么要文件，不是替医生说话）。
// name/groupNumber 细分随后端枚举归并收敛（PATIENT_NOT_FOUND 吸收姓名不匹配；68/76/78 被后端丢弃）
export const buildCardRequestNote = (
    payerName: string,
    aaa?: TAaaEntry,
    context?: TCardRequestContext,
    // failedPlan 点名的那张：档案上的 "{Carrier} {Plan}"（多份 primary 时病人得知道重传哪一张）
    planName?: string,
): string => {
    const upload = 'Please upload a clear photo of the front and back of your current insurance card.';
    if (!aaa) {
        if (context === 'failedPlan') {
            return (
                `Please upload a clear photo of the front and back of your ${planName || payerName} card. ` +
                'We couldn’t verify this plan, and a new photo helps us confirm your coverage before your visit. ' +
                'If your insurance has changed, upload your new card instead.'
            );
        }
        return `${upload} This helps us verify your coverage before your visit.`;
    }
    const why =
        aaa.code === 'PATIENT_NOT_FOUND'
            ? `${payerName} could not find you with the details we have on file, so your coverage could not be checked. This does not mean you are uninsured.`
            : `We could not verify your coverage because the member ID on file did not match ${payerName}’s records. This does not mean you are uninsured.`;
    return `${why}\n\n${upload}`;
};

// unmapped payer 流：选定候选后的患者确认草稿（问的是路由决定，不请求文件、无复查承诺）
export const buildRouteConfirmDraft = (
    patientFirstName: string,
    picked: TPayerCandidate,
    memberIdOnFile?: string,
): string =>
    `Hi ${patientFirstName},\n\n` +
    'I was checking your insurance coverage and I need to confirm which plan to bill it under. ' +
    `I am using ${picked.name}${memberIdOnFile ? `, member ID ${memberIdOnFile}` : ''}, based on what I have on file.\n\n` +
    'Could you confirm that is the right plan?';
